import React, { useState, useContext, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { AuthContext } from '../contexts/AuthContext';
import AlertModal from '../components/AlertModal';
import PremiumSelect from '../components/PremiumSelect';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { AlertState, FinanceReportResponse, FinanceTransactionType } from '../types';
import './Reports.css';

const PERIOD_CHART_LEGEND = [
  { value: 'Receitas', type: 'line' as const, color: '#16a34a' },
  { value: 'Despesas', type: 'line' as const, color: '#dc2626' },
];

interface ReportFiltersState {
  startDate: string;
  endDate: string;
  year: string;
  cycleId: string;
  type: 'all' | FinanceTransactionType;
  accountId: string;
  sourceId: string;
  categoryId: string;
  tagId: string;
  search: string;
}

const Reports: React.FC = () => {
  const authContext = useContext(AuthContext);
  const { user } = authContext || {};
  const [reportData, setReportData] = useState<FinanceReportResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [exportingFormat, setExportingFormat] = useState<'pdf' | 'xlsx' | null>(null);
  const [alert, setAlert] = useState<AlertState>({ isOpen: false, message: '', type: 'error' });
  const [filters, setFilters] = useState<ReportFiltersState>({
    startDate: '',
    endDate: '',
    year: '',
    cycleId: '',
    type: 'all',
    accountId: '',
    sourceId: '',
    categoryId: '',
    tagId: '',
    search: '',
  });

  const canAccess = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'hr';

  const buildParams = useCallback(
    (state: ReportFiltersState) => ({
      startDate: state.startDate || undefined,
      endDate: state.endDate || undefined,
      year: state.year || undefined,
      cycleId: state.cycleId || undefined,
      type: state.type !== 'all' ? state.type : undefined,
      accountId: state.accountId || undefined,
      sourceId: state.sourceId || undefined,
      categoryId: state.categoryId || undefined,
      tagId: state.tagId || undefined,
      search: state.search || undefined,
    }),
    [],
  );

  const loadReport = useCallback(async (state: ReportFiltersState): Promise<void> => {
    if (!user || !canAccess) return;
    setLoading(true);
    try {
      const response = await api.get<FinanceReportResponse>('/reports/finance', {
        params: buildParams(state),
      });
      setReportData(response.data);
    } catch (error) {
      console.error('Erro ao gerar relatório:', error);
      setAlert({
        isOpen: true,
        message: 'Erro ao gerar o relatório financeiro',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [buildParams, canAccess, user]);

  useEffect(() => {
    if (user && canAccess) {
      loadReport(filters);
    }
  }, [canAccess, filters, loadReport, user]);

  const exportReport = async (format: 'pdf' | 'xlsx'): Promise<void> => {
    if (!canAccess) return;

    try {
      setExportingFormat(format);
      const response = await api.get(`/reports/finance/${format}`, {
        params: buildParams(filters),
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `relatorio-financeiro.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao exportar relatório:', error);
      setAlert({
        isOpen: true,
        message: 'Erro ao exportar o relatório financeiro',
        type: 'error',
      });
    } finally {
      setExportingFormat(null);
    }
  };

  const filterOptions = reportData?.filters.options;

  const yearSelectOptions = useMemo(
    () => [
      { value: '', label: 'Todos' },
      ...(filterOptions?.years.map((y) => ({ value: String(y), label: String(y) })) || []),
    ],
    [filterOptions?.years],
  );

  const cycleSelectOptions = useMemo(
    () => [
      { value: '', label: 'Todos' },
      ...(filterOptions?.cycles.map((c) => ({ value: c.id, label: c.name })) || []),
    ],
    [filterOptions?.cycles],
  );

  const typeSelectOptions = useMemo(
    () => [
      { value: 'all', label: 'Todos' },
      { value: 'income', label: 'Receitas' },
      { value: 'expense', label: 'Despesas' },
      { value: 'tithe', label: 'Dízimo' },
    ],
    [],
  );

  const accountSelectOptions = useMemo(
    () => [
      { value: '', label: 'Todas' },
      ...(filterOptions?.accounts.map((a) => ({ value: a.id, label: a.name })) || []),
    ],
    [filterOptions?.accounts],
  );

  const sourceSelectOptions = useMemo(
    () => [
      { value: '', label: 'Todas' },
      ...(filterOptions?.sources.map((s) => ({ value: s.id, label: s.name })) || []),
    ],
    [filterOptions?.sources],
  );

  const categorySelectOptions = useMemo(
    () => [
      { value: '', label: 'Todas' },
      ...(filterOptions?.categories.map((c) => ({ value: c.id, label: c.name })) || []),
    ],
    [filterOptions?.categories],
  );

  const tagSelectOptions = useMemo(
    () => [
      { value: '', label: 'Todas' },
      ...(filterOptions?.tags.map((t) => ({ value: t.id, label: t.name })) || []),
    ],
    [filterOptions?.tags],
  );

  const periodChartData = useMemo(
    () => [...(reportData?.groupedByPeriod ?? [])].reverse(),
    [reportData?.groupedByPeriod],
  );

  if (!user) {
    return (
      <div className="reports-state">
        <div className="reports-state-card">
          <span className="reports-badge">Carregando</span>
          <h1>Preparando relatórios</h1>
          <p>Validando seu perfil e carregando opções de filtro.</p>
        </div>
      </div>
    );
  }

  if (!canAccess) {
    return (
      <div className="reports-state">
        <div className="reports-state-card">
          <span className="reports-badge">Acesso</span>
          <h1>Relatórios restritos</h1>
          <p>Os relatórios financeiros estão disponíveis apenas para gestão, RH e administração.</p>
        </div>
      </div>
    );
  }

  const formatCurrency = (value: number): string =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  const formatPercent = (value: number | null): string =>
    value === null
      ? 'Sem base'
      : `${value.toLocaleString('pt-BR', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}%`;

  const formatDate = (value: string): string => new Date(value).toLocaleDateString('pt-BR');
  const groupedCategories =
    [...(reportData?.groupedByCategory || [])]
      .filter((item) => item.expense > 0)
      .sort((a, b) => b.expense - a.expense)
      .slice(0, 8) || [];
  const groupedSources =
    [...(reportData?.groupedBySource || [])]
      .filter((item) => item.income > 0)
      .sort((a, b) => b.income - a.income)
      .slice(0, 8) || [];
  const comparison = reportData?.comparison;

  const getComparisonTone = (
    metric: 'income' | 'expense' | 'balance' | 'transactionsCount',
  ): 'is-positive' | 'is-attention' | 'is-neutral' => {
    const difference = comparison?.[metric].difference || 0;
    if (difference === 0) {
      return 'is-neutral';
    }
    if (metric === 'expense') {
      return difference < 0 ? 'is-positive' : 'is-attention';
    }
    return difference > 0 ? 'is-positive' : 'is-attention';
  };

  return (
    <div className="reports-page">
      <section className="reports-hero">
        <div className="reports-hero-copy">
          <span className="reports-badge">Indicadores</span>
          <h1>Relatórios</h1>
          <p>
            Filtre lançamentos por período, ciclo, contas e classificações; compare com o período
            anterior, visualize distribuições e exporte em Excel ou PDF conforme seu papel na
            organização.
          </p>
        </div>
      </section>

      <section className="reports-surface">
        <div className="reports-section-header">
          <div>
            <span className="reports-section-kicker">Filtros</span>
            <h2>Recorte analítico</h2>
          </div>
          <p className="reports-section-lead">
            Todos os gráficos, tabelas e exportações respeitam os filtros abaixo.
          </p>
        </div>

        <div className="reports-finance-filter-grid premium-filters">
          <div className="reports-filter-group">
            <label htmlFor="report-startDate">Data inicial</label>
            <input
              id="report-startDate"
              type="date"
              value={filters.startDate}
              onChange={(e) => setFilters((current) => ({ ...current, startDate: e.target.value }))}
            />
          </div>

          <div className="reports-filter-group">
            <label htmlFor="report-endDate">Data final</label>
            <input
              id="report-endDate"
              type="date"
              value={filters.endDate}
              onChange={(e) => setFilters((current) => ({ ...current, endDate: e.target.value }))}
            />
          </div>

          <PremiumSelect
            id="report-year"
            label="Ano"
            wrapperClassName="premium-select-field"
            value={filters.year}
            options={yearSelectOptions}
            onChange={(value) => setFilters((current) => ({ ...current, year: value }))}
            placeholder="Todos"
          />

          <PremiumSelect
            id="report-cycle"
            label="Período"
            wrapperClassName="premium-select-field"
            value={filters.cycleId}
            options={cycleSelectOptions}
            onChange={(value) => setFilters((current) => ({ ...current, cycleId: value }))}
            placeholder="Todos"
          />

          <PremiumSelect
            id="report-type"
            label="Tipo"
            wrapperClassName="premium-select-field"
            value={filters.type}
            options={typeSelectOptions}
            onChange={(value) =>
              setFilters((current) => ({
                ...current,
                type: value as 'all' | FinanceTransactionType,
              }))
            }
            placeholder="Todos"
          />

          <PremiumSelect
            id="report-account"
            label="Conta"
            wrapperClassName="premium-select-field"
            value={filters.accountId}
            options={accountSelectOptions}
            onChange={(value) => setFilters((current) => ({ ...current, accountId: value }))}
            placeholder="Todas"
          />

          <PremiumSelect
            id="report-source"
            label="Fonte"
            wrapperClassName="premium-select-field"
            value={filters.sourceId}
            options={sourceSelectOptions}
            onChange={(value) => setFilters((current) => ({ ...current, sourceId: value }))}
            placeholder="Todas"
          />

          <PremiumSelect
            id="report-category"
            label="Categoria"
            wrapperClassName="premium-select-field"
            value={filters.categoryId}
            options={categorySelectOptions}
            onChange={(value) => setFilters((current) => ({ ...current, categoryId: value }))}
            placeholder="Todas"
          />

          <PremiumSelect
            id="report-tag"
            label="Tag"
            wrapperClassName="premium-select-field"
            value={filters.tagId}
            options={tagSelectOptions}
            onChange={(value) => setFilters((current) => ({ ...current, tagId: value }))}
            placeholder="Todas"
          />

          <div className="reports-filter-search-row reports-filter-group-span-2">
            <div className="reports-filter-group reports-filter-search-field">
              <label htmlFor="report-search">Busca</label>
              <input
                id="report-search"
                type="text"
                placeholder="Descrição, categoria, fonte, conta, tag..."
                value={filters.search}
                onChange={(e) => setFilters((current) => ({ ...current, search: e.target.value }))}
              />
            </div>
            <div className="reports-filter-clear">
              <button
                type="button"
                className="premium-filter-reset reports-filter-reset-btn"
                onClick={() =>
                  setFilters({
                    startDate: '',
                    endDate: '',
                    year: '',
                    cycleId: '',
                    type: 'all',
                    accountId: '',
                    sourceId: '',
                    categoryId: '',
                    tagId: '',
                    search: '',
                  })
                }
              >
                Limpar filtros
              </button>
            </div>
          </div>
        </div>

        <div className="report-actions">
          <button
            type="button"
            className="reports-action-button"
            onClick={() => exportReport('xlsx')}
            disabled={loading || exportingFormat !== null}
          >
            {exportingFormat === 'xlsx' ? 'Exportando Excel...' : 'Exportar Excel'}
          </button>
          <button
            type="button"
            className="reports-action-button is-secondary"
            onClick={() => exportReport('pdf')}
            disabled={loading || exportingFormat !== null}
          >
            {exportingFormat === 'pdf' ? 'Exportando PDF...' : 'Exportar PDF'}
          </button>
        </div>
      </section>

      <section className="reports-stats-grid">
        <article className="reports-stat-card">
          <span>Receitas</span>
          <strong>{formatCurrency(reportData?.summary.totalIncome || 0)}</strong>
          <p>Total de entradas no recorte filtrado.</p>
        </article>
        <article className="reports-stat-card">
          <span>Despesas</span>
          <strong>{formatCurrency(reportData?.summary.totalExpense || 0)}</strong>
          <p>Total de saídas classificadas no período analisado.</p>
        </article>
        <article className="reports-stat-card">
          <span>Saldo</span>
          <strong>{formatCurrency(reportData?.summary.balance || 0)}</strong>
          <p>Resultado líquido consolidado do recorte atual.</p>
        </article>
        <article className="reports-stat-card">
          <span>Lançamentos</span>
          <strong>{reportData?.summary.transactionsCount || 0}</strong>
          <p>Quantidade de registros presentes nos relatórios.</p>
        </article>
      </section>

      <section className="reports-surface">
        <div className="reports-section-header">
          <div>
            <span className="reports-section-kicker">Comparativo</span>
            <h2>
              {comparison?.currentPeriodLabel || 'Período atual'} x{' '}
              {comparison?.previousPeriodLabel || 'Sem base anterior'}
            </h2>
          </div>
          <p className="reports-section-lead">
            Os indicadores abaixo comparam o período atual do recorte com o imediatamente anterior.
          </p>
        </div>

        <div className="reports-comparison-grid">
          <article className={`reports-comparison-card ${getComparisonTone('income')}`}>
            <span>Receitas</span>
            <strong>{formatCurrency(comparison?.income.current || 0)}</strong>
            <small>Anterior: {formatCurrency(comparison?.income.previous || 0)}</small>
            <div className={`reports-comparison-delta ${getComparisonTone('income')}`}>
              <b>{(comparison?.income.difference || 0) >= 0 ? '+' : ''}{formatCurrency(comparison?.income.difference || 0)}</b>
              <span>{formatPercent(comparison?.income.percentChange ?? null)}</span>
            </div>
          </article>

          <article className={`reports-comparison-card ${getComparisonTone('expense')}`}>
            <span>Despesas</span>
            <strong>{formatCurrency(comparison?.expense.current || 0)}</strong>
            <small>Anterior: {formatCurrency(comparison?.expense.previous || 0)}</small>
            <div className={`reports-comparison-delta ${getComparisonTone('expense')}`}>
              <b>{(comparison?.expense.difference || 0) >= 0 ? '+' : ''}{formatCurrency(comparison?.expense.difference || 0)}</b>
              <span>{formatPercent(comparison?.expense.percentChange ?? null)}</span>
            </div>
          </article>

          <article className={`reports-comparison-card ${getComparisonTone('balance')}`}>
            <span>Saldo</span>
            <strong>{formatCurrency(comparison?.balance.current || 0)}</strong>
            <small>Anterior: {formatCurrency(comparison?.balance.previous || 0)}</small>
            <div className={`reports-comparison-delta ${getComparisonTone('balance')}`}>
              <b>{(comparison?.balance.difference || 0) >= 0 ? '+' : ''}{formatCurrency(comparison?.balance.difference || 0)}</b>
              <span>{formatPercent(comparison?.balance.percentChange ?? null)}</span>
            </div>
          </article>

          <article className={`reports-comparison-card ${getComparisonTone('transactionsCount')}`}>
            <span>Lançamentos</span>
            <strong>{comparison?.transactionsCount.current || 0}</strong>
            <small>Anterior: {comparison?.transactionsCount.previous || 0}</small>
            <div className={`reports-comparison-delta ${getComparisonTone('transactionsCount')}`}>
              <b>{(comparison?.transactionsCount.difference || 0) >= 0 ? '+' : ''}{comparison?.transactionsCount.difference || 0}</b>
              <span>{formatPercent(comparison?.transactionsCount.percentChange ?? null)}</span>
            </div>
          </article>
        </div>
      </section>

      <section className="reports-chart-grid">
        <article className="reports-surface">
          <div className="reports-section-header">
            <div>
              <span className="reports-section-kicker">Períodos</span>
              <h2>Receitas e despesas ao longo do tempo</h2>
            </div>
          </div>
          <div className="reports-chart-wrap">
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={periodChartData}>
                <defs>
                  <linearGradient id="reportIncomeFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#16a34a" stopOpacity={0.32} />
                    <stop offset="95%" stopColor="#16a34a" stopOpacity={0.04} />
                  </linearGradient>
                  <linearGradient id="reportExpenseFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#dc2626" stopOpacity={0.28} />
                    <stop offset="95%" stopColor="#dc2626" stopOpacity={0.04} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#dbe7ff" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} />
                <Tooltip cursor={false} formatter={(value: number, name: string) => [formatCurrency(Number(value)), name]} />
                <Legend payload={PERIOD_CHART_LEGEND} />
                <Area
                  type="monotone"
                  dataKey="expense"
                  name="Despesas"
                  stroke="#dc2626"
                  fill="url(#reportExpenseFill)"
                  strokeWidth={3}
                />
                <Area
                  type="monotone"
                  dataKey="income"
                  name="Receitas"
                  stroke="#16a34a"
                  fill="url(#reportIncomeFill)"
                  strokeWidth={3}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </article>

        <article className="reports-surface">
          <div className="reports-section-header">
            <div>
              <span className="reports-section-kicker">Categorias</span>
              <h2>Despesas por categoria</h2>
            </div>
          </div>
          <div className="reports-chart-wrap">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={groupedCategories}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#dbe7ff" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} />
                <Tooltip cursor={false} formatter={(value: number) => formatCurrency(value)} />
                <Bar dataKey="expense" name="Despesas" fill="#dc2626" radius={[10, 10, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </article>
      </section>

      <section className="reports-chart-grid">
        <article className="reports-surface">
          <div className="reports-section-header">
            <div>
              <span className="reports-section-kicker">Fontes</span>
              <h2>Receitas por fonte</h2>
            </div>
          </div>
          <div className="reports-chart-wrap">
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={groupedSources}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#dbe7ff" />
                <XAxis dataKey="label" tickLine={false} axisLine={false} />
                <YAxis tickLine={false} axisLine={false} />
                <Tooltip cursor={false} formatter={(value: number) => formatCurrency(value)} />
                <Bar dataKey="income" name="Receitas" fill="#16a34a" radius={[10, 10, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </article>

        <article className="reports-surface">
          <div className="reports-section-header">
            <div>
              <span className="reports-section-kicker">Consolidados</span>
              <h2>Tabela por categoria</h2>
            </div>
          </div>
          <div className="reports-table-wrap">
            <table className="reports-table">
              <thead>
                <tr>
                  <th>Categoria</th>
                  <th>Receitas</th>
                  <th>Despesas</th>
                  <th>Saldo</th>
                  <th>Qtd.</th>
                </tr>
              </thead>
              <tbody>
                {(reportData?.groupedByCategory || []).map((item) => (
                  <tr key={item.label}>
                    <td>{item.label}</td>
                    <td>{formatCurrency(item.income)}</td>
                    <td>{formatCurrency(item.expense)}</td>
                    <td>{formatCurrency(item.balance)}</td>
                    <td>{item.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </article>
      </section>

      <section className="reports-surface">
        <div className="reports-section-header">
          <div>
            <span className="reports-section-kicker">Detalhamento</span>
            <h2>Lançamentos filtrados</h2>
          </div>
          <p className="reports-section-lead">
            {reportData?.transactions.length || 0} lançamento(s) listados no recorte atual.
          </p>
        </div>

        <div className="reports-table-wrap">
          <table className="reports-table reports-table-wide">
            <thead>
              <tr>
                <th>Data</th>
                <th>Descrição</th>
                <th>Tipo</th>
                <th>Período</th>
                <th>Conta</th>
                <th>Fonte</th>
                <th>Categoria</th>
                <th>Tags</th>
                <th>Valor</th>
              </tr>
            </thead>
            <tbody>
              {(reportData?.transactions || []).map((transaction) => (
                <tr key={transaction.id}>
                  <td>{formatDate(transaction.transactionDate)}</td>
                  <td>{transaction.description}</td>
                  <td>
                    {transaction.type === 'income'
                      ? 'Receita'
                      : transaction.type === 'tithe'
                        ? 'Dízimo'
                        : 'Despesa'}
                  </td>
                  <td>{transaction.period}</td>
                  <td>{transaction.accountName}</td>
                  <td>{transaction.sourceName}</td>
                  <td>{transaction.categoryName}</td>
                  <td>{transaction.tags.join(', ') || '-'}</td>
                  <td>{formatCurrency(transaction.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert((current) => ({ ...current, isOpen: false }))}
        message={alert.message}
        type={alert.type}
      />
    </div>
  );
};

export default Reports;
